import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, X, ScanLine, AlertCircle } from 'lucide-react';

const QRScanner = ({ isOpen, onClose, onScan, title = "Scan QR Code" }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      initializeCameras();
    }
    return () => {
      stopScanning();
    };
  }, [isOpen]);

  const initializeCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length > 0) {
        setCameras(devices);
        // Prefer back camera on mobile
        const backCamera = devices.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear'));
        setSelectedCamera(backCamera || devices[0]);
        setError(null);
      } else {
        setError('No cameras found on this device');
      }
    } catch (err) {
      console.error('Error getting cameras:', err);
      setError('Unable to access camera. Please ensure camera permissions are granted.');
    }
  };

  const startScanning = async () => {
    if (!selectedCamera) return;

    try {
      setError(null);
      const html5Qrcode = new Html5Qrcode('qr-reader');
      html5QrcodeRef.current = html5Qrcode;

      await html5Qrcode.start(
        selectedCamera.id,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          // Success callback
          handleSuccessfulScan(decodedText);
        },
        (errorMessage) => {
          // Scan error - usually just means no QR code detected yet
        }
      );
      
      setIsScanning(true);
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Failed to start camera. Please try again.');
    }
  };

  const stopScanning = async () => {
    try {
      if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current.clear();
      }
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
    setIsScanning(false);
  };

  const handleSuccessfulScan = async (decodedText) => {
    // Stop scanning immediately after successful read
    await stopScanning();
    
    // Call the onScan callback with the decoded text
    if (onScan) {
      onScan(decodedText);
    }
  };

  const handleClose = async () => {
    await stopScanning();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-sm text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Camera selector */}
          {cameras.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {cameras.map((camera) => (
                <Button
                  key={camera.id}
                  variant={selectedCamera?.id === camera.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    stopScanning();
                    setSelectedCamera(camera);
                  }}
                  className="text-xs"
                >
                  {camera.label.substring(0, 20)}...
                </Button>
              ))}
            </div>
          )}

          {/* Scanner viewport */}
          <div className="relative">
            <div 
              id="qr-reader" 
              ref={scannerRef}
              className="w-full aspect-square bg-slate-900 rounded-sm overflow-hidden"
            />
            
            {!isScanning && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 text-white">
                <ScanLine className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-sm text-slate-400 mb-4">
                  {cameras.length > 0 ? 'Click Start to begin scanning' : 'Initializing camera...'}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="rounded-sm"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            
            {!isScanning ? (
              <Button
                onClick={startScanning}
                disabled={!selectedCamera}
                className="rounded-sm bg-orange-500 hover:bg-orange-600"
                data-testid="start-scan-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Start Scanning
              </Button>
            ) : (
              <Button
                onClick={stopScanning}
                variant="destructive"
                className="rounded-sm"
              >
                <X className="w-4 h-4 mr-2" />
                Stop
              </Button>
            )}
          </div>

          <p className="text-xs text-slate-500 text-center">
            Point your camera at a QR code to scan
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRScanner;
